
import React, { useState } from 'react';
import { Job } from "@/components/jobs/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Edit3, Save, X } from 'lucide-react';
import { useUpdateJob } from '@/hooks/mutations/useUpdateJob';
import { toast } from 'sonner';

interface EditableJobDescriptionProps {
  job: Job;
  canEdit: boolean;
}

export const EditableJobDescription: React.FC<EditableJobDescriptionProps> = ({ job, canEdit }) => {
  const { mutate: updateJob, isPending: isUpdating } = useUpdateJob();
  const [isEditing, setIsEditing] = useState(false);
  const [editDescription, setEditDescription] = useState(job.description || '');

  const handleSave = async () => {
    updateJob({ id: job.id, data: { description: editDescription } }, {
      onSuccess: () => {
        toast.success('Job description updated successfully');
        setIsEditing(false);
      }
    });
  };

  const handleCancel = () => {
    setEditDescription(job.description || '');
    setIsEditing(false);
  };

  if (!job.description && !canEdit) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Job Description</CardTitle>
          {canEdit && !isEditing && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(true)}
            >
              <Edit3 className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <div className="space-y-4">
            <Textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder="Enter job description..."
              className="min-h-[200px]"
            />
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={isUpdating}>
                <Save className="h-4 w-4 mr-2" />
                {isUpdating ? 'Saving...' : 'Save'}
              </Button>
              <Button variant="outline" onClick={handleCancel} disabled={isUpdating}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="whitespace-pre-wrap text-sm">
            {job.description || (canEdit && (
              <div className="text-muted-foreground italic">
                No description available. Click Edit to add one.
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
